import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery, GET_CENSUS_TRADE_DATA, SEARCH_HTS_CODES, CALCULATE_DUTY_RATES_FOR_COUNTRIES } from "@/graphql";

interface CensusCountryData {
  country: string;
  totalValue: number;
  percentage: number;
  totalQuantity?: number;
  unit?: string;
  unitPrice?: number;
}

interface CensusTradeDataResponse {
  getCensusTradeData: {
    totalValue: number;
    year: number | null;
    dataByCountry: CensusCountryData[];
  };
}

interface HtsSearchResult {
  hts_code: string;
  description: string;
}

interface SearchHtsCodesResponse {
  searchHtsCodes: HtsSearchResult[];
}

interface TariffBreakdownItem {
  name: string;
  rate: number;
  authority?: string;
}

interface CountryDutyRate {
  country: string;
  countryCode?: string;
  totalDutyRate: number;
  generalDutyRate: number;
  additionalDutiesRate: number;
  tariffBreakdown: TariffBreakdownItem[];
  error?: string;
}

interface CalculateDutyRatesResponse {
  calculateDutyRatesForCountries: {
    htsCode: string;
    effectiveDate?: string;
    htsDescription?: string;
    countryDutyRates: CountryDutyRate[];
  };
}

export const censusTradeDataApi = createApi({
  reducerPath: "api/censusTradeData",
  tagTypes: ["censusTradeData", "dutyRates"],
  baseQuery,
  endpoints: (builder) => ({
    getCensusTradeData: builder.query<
      CensusTradeDataResponse,
      { htsCode?: string; year?: number; limit?: number }
    >({
      query: (variables) => ({
        document: GET_CENSUS_TRADE_DATA,
        variables,
      }),
      providesTags: ["censusTradeData"],
    }),
    searchHtsCodes: builder.query<
      SearchHtsCodesResponse,
      { query: string; limit?: number }
    >({
      query: (variables) => ({
        document: SEARCH_HTS_CODES,
        variables,
      }),
    }),
    calculateDutyRatesForCountries: builder.query<
      CalculateDutyRatesResponse,
      { htsCode: string; countries: string[]; effectiveDate?: string }
    >({
      query: (variables) => ({
        document: CALCULATE_DUTY_RATES_FOR_COUNTRIES,
        variables,
      }),
      providesTags: ["dutyRates"],
    }),
  }),
});

export const { 
  useGetCensusTradeDataQuery, 
  useSearchHtsCodesQuery, 
  useLazySearchHtsCodesQuery,
  useCalculateDutyRatesForCountriesQuery,
  useLazyCalculateDutyRatesForCountriesQuery,
} = censusTradeDataApi;
